﻿import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAdvancedForm } from 'helpers/form/useAdvancedForm';
import { FormError } from 'components/uikit/FormError';
import { Loading } from 'components/uikit/suspense/Loading';
import {
  CreateProjectDto,
  PatchProjectDto,
  TeamMemberDto,
} from 'services/api/api-client';
import { QueryFactory } from 'services/api';
import { Button, ButtonColor } from 'components/uikit/buttons/Button';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import styles from './ProjectModal.module.scss';
import { CustomModal } from 'components/uikit/modal/CustomModal';
import { useModal } from 'components/uikit/modal/useModal.tsx';
import { useAuth } from 'helpers/auth/auth-interceptor.ts';
import { ProjectMainTab } from './ProjectMainTab.tsx';
import { ProjectAITab } from './ProjectAITab.tsx';
import { ProjectModalTitle } from './ProjectModalTitle.tsx';

type AddProjectModalProps = {
  onClose: () => void;
  isOpen: boolean;
  id?: number;
};

export enum ProjectTab {
  Main,
  AI,
}

export const ProjectModal: React.FC<AddProjectModalProps> = (props) => {
  const isEditing = !!props.id;

  const modalTranslation = useScopedTranslation('Component.CommonModals');
  const buttonTitle = useScopedTranslation('Button.Title');
  const queryClient = useQueryClient();
  const modal = useModal();
  const authData = useAuth();
  const currentUserId =
    authData && authData !== 'loading' ? authData.claims.id : null;

  const projectQuery = QueryFactory.ProjectQuery.useGetQuery(props.id!, {
    enabled: isEditing && props.isOpen,
    throwOnError: false,
  });

  const [showOverallErrors, setShowOverallErrors] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMemberDto[]>([]);
  const [selectedTab, setSelectedTab] = useState<ProjectTab>(ProjectTab.Main);

  const form = useAdvancedForm<CreateProjectDto>(
    useCallback(
      async (data) => {
        setShowOverallErrors(true);

        if (data.isPrivate)
          data.teamMembers = teamMembers.map((tm) => ({ ...tm }));

        if (!isEditing) {
          await QueryFactory.ProjectClient.create(data);
        } else {
          let patchData = data as PatchProjectDto;

          const isExportedAndNameChanged =
            projectQuery.data?.isExported &&
            projectQuery.data?.name !== patchData.name;

          patchData.name =
            projectQuery.data?.name === data.name ? undefined : data.name;
          patchData.description =
            projectQuery.data?.description === data.description
              ? undefined
              : data.description;
          patchData.iconCode =
            projectQuery.data?.iconCode === data.iconCode
              ? undefined
              : data.iconCode;

          let confirmed = true;
          if (isExportedAndNameChanged) {
            confirmed = await modal.showConfirm({
              title: modalTranslation.t('confirm_change'),
              text: modalTranslation.t('confirm_change_message'),
            });
          }

          if (confirmed) {
            await QueryFactory.ProjectClient.patch(props.id!, patchData);
            await queryClient.invalidateQueries({
              queryKey: QueryFactory.ProjectQuery.getQueryKey(props.id!),
            });
          }
        }

        await queryClient.invalidateQueries({
          queryKey: QueryFactory.ProjectQuery.searchQueryKey(),
        });

        onModalClose();
      },
      [teamMembers, projectQuery.data],
    ),
    { defaultValues: projectQuery.data },
  );

  const onModalClose = () => {
    props.onClose();
    form.reset();
    form.clearErrors();
    setShowOverallErrors(false);
  };

  useEffect(() => {
    if (props.isOpen) {
      setTimeout(() => {
        form.setFocus('name');
      });
    }
  }, [form, props.isOpen]);

  const isPrivate = form.watch('isPrivate');

  const availableTeamMembersQuery =
    QueryFactory.ProjectTeamQuery.useGetAvailableTeamMembersQuery({
      enabled: props.isOpen,
    });
  const availableTeamMembers = useMemo(() => {
    if (!availableTeamMembersQuery.data || !currentUserId) return [];

    return !isEditing
      ? availableTeamMembersQuery.data.filter(
          (tm) =>
            !teamMembers.some((_tm) => _tm.id === tm.id) &&
            tm.id !== currentUserId,
        )
      : availableTeamMembersQuery.data;
  }, [availableTeamMembersQuery.data, teamMembers, currentUserId]);

  // Setting to teamMembers users from query on modal open.
  useEffect(() => {
    if (!props.isOpen || !projectQuery.data?.teamMembers) return;

    const existing =
      projectQuery.data?.teamMembers?.map((tm) => ({ ...tm })) ?? [];

    setTeamMembers(existing);
  }, [props.isOpen, projectQuery.data?.teamMembers]);

  return (
    <CustomModal
      isOpen={props.isOpen}
      onClose={onModalClose}
      isBlocking={true}
      title={
        <ProjectModalTitle
          isEditing={isEditing}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
        />
      }
      classes={{ content: styles.modalContent, header: styles.modalHeader }}
    >
      <Loading
        loading={
          form.formState.isSubmitting || availableTeamMembersQuery.isLoading
        }
      >
        <form onSubmit={form.handleSubmitDefault} className={styles.form}>
          {selectedTab === ProjectTab.Main && (
            <ProjectMainTab
              form={form}
              projectIconCode={projectQuery.data?.iconCode}
              isPrivate={isPrivate}
              availableTeamMembers={availableTeamMembers}
              teamMembers={teamMembers}
              currentUserId={currentUserId}
              setTeamMembers={setTeamMembers}
            />
          )}
          {selectedTab === ProjectTab.AI && <ProjectAITab form={form} />}
          <FormError>{showOverallErrors ? form.overallError : ''}</FormError>
          <div className={styles.footer}>
            <Button
              className={styles.button}
              color={ButtonColor.Secondary}
              title={buttonTitle.t('cancel')}
              onClick={onModalClose}
            />
            <Button
              className={styles.button}
              color={ButtonColor.Default}
              type={'submit'}
              title={buttonTitle.t('save')}
            />
          </div>
        </form>
      </Loading>
    </CustomModal>
  );
};
